import { useState, useEffect } from "react";
import { Star, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

interface AddReviewFormProps {
  productId: string;
  onReviewAdded: () => void;
  onCancel: () => void;
}

const AddReviewForm = ({ productId, onReviewAdded, onCancel }: AddReviewFormProps) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existingReview, setExistingReview] = useState<any>(null);
  const [checkingReview, setCheckingReview] = useState(true);

  useEffect(() => {
    checkExistingReview();
  }, [productId, user]);

  const checkExistingReview = async () => {
    if (!user) {
      setCheckingReview(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking existing review:', error);
      }

      setExistingReview(data);
    } catch (error) {
      console.error('Error checking existing review:', error);
    } finally {
      setCheckingReview(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error("Vous devez être connecté pour laisser un avis");
      return;
    }

    if (rating === 0) {
      toast.error("Veuillez sélectionner une note");
      return;
    }

    if (!comment.trim()) {
      toast.error("Veuillez écrire un commentaire");
      return;
    }

    try {
      setSubmitting(true);

      // Check if user has purchased this product
      const { data: purchaseData, error: purchaseError } = await supabase
        .from('orders')
        .select('id')
        .eq('client_id', user.id)
        .eq('product_id', productId)
        .eq('status', 'PICKED_UP')
        .limit(1);

      if (purchaseError) {
        console.error('Error checking purchase:', purchaseError);
      }

      const hasVerifiedPurchase = purchaseData && purchaseData.length > 0;

      const { error } = await supabase
        .from('product_reviews')
        .insert({
          product_id: productId,
          user_id: user.id,
          rating,
          title: title.trim() || null,
          comment: comment.trim(),
          verified_purchase: hasVerifiedPurchase
        });

      if (error) {
        // Check if error is due to unique constraint violation
        if (error.code === '23505') {
          toast.error("Vous avez déjà laissé un avis pour ce produit");
        } else {
          throw error;
        }
        return;
      }

      toast.success("Votre avis a été publié avec succès");
      onReviewAdded();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error("Erreur lors de la publication de l'avis");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStarInput = () => {
    return (
      <div className="flex items-center gap-2">
        <Label>Note:</Label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              className="transition-colors hover:scale-110 transform"
            >
              <Star
                className={`h-8 w-8 ${
                  star <= (hoveredRating || rating)
                    ? "fill-primary text-primary"
                    : "text-muted-foreground hover:text-primary"
                }`}
              />
            </button>
          ))}
        </div>
        <span className="text-sm text-muted-foreground ml-2">
          {rating > 0 && (
            <>
              {rating} étoile{rating !== 1 ? 's' : ''} - {
                rating === 5 ? "Excellent" :
                rating === 4 ? "Très bien" :
                rating === 3 ? "Bien" :
                rating === 2 ? "Moyen" : "Décevant"
              }
            </>
          )}
        </span>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Écrire un avis</CardTitle>
      </CardHeader>
      <CardContent>
        {checkingReview ? (
          <div className="py-8 text-center text-muted-foreground">
            Chargement...
          </div>
        ) : existingReview ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Vous avez déjà noté ce produit</AlertTitle>
            <AlertDescription>
              Vous avez déjà laissé un avis pour ce produit le{" "}
              {new Date(existingReview.created_at).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              . Vous ne pouvez laisser qu'un seul avis par produit.
            </AlertDescription>
            <div className="mt-4">
              <Button variant="outline" onClick={onCancel}>
                Fermer
              </Button>
            </div>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating Input */}
            <div className="space-y-2">
              {renderStarInput()}
            </div>

            {/* Title Input */}
            <div className="space-y-2">
              <Label htmlFor="review-title">
                Titre de votre avis (optionnel)
              </Label>
              <Input
                id="review-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Résumez votre expérience en quelques mots"
                maxLength={100}
              />
            </div>

            {/* Comment Input */}
            <div className="space-y-2">
              <Label htmlFor="review-comment">
                Votre avis <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Partagez votre expérience avec ce produit. Qu'avez-vous aimé ou moins aimé ?"
                rows={6}
                required
                minLength={10}
                maxLength={1000}
              />
              <div className="text-sm text-muted-foreground text-right">
                {comment.length}/1000 caractères
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={submitting || rating === 0}
              >
                {submitting ? "Publication..." : "Publier l'avis"}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default AddReviewForm;